import { useState, useMemo } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useTypedDispatch, useTypedSelector } from "@lib/redux";
import { setDateBetween as setDateBetweenAction } from "@lib/redux/slices/cache";
import { AppConst } from "@lib/constants";

export type DateRangeType = "daily" | "weekly" | "monthly";

export interface UseDateRangeOptions {
  initialRange?: DateRangeType;
  customStartDate?: string;
  customEndDate?: string;
  cache?: boolean;
}

export interface UseDateRangeReturn {
  dateBetween: [string, string];
  setDateBetween: (date: [Dayjs, Dayjs]) => void;
  setRangeType: (range: DateRangeType) => void;
  currentRangeType: DateRangeType;
  resetToRange: (range: DateRangeType) => void;
}

const getDateRangeByType = (rangeType: DateRangeType): [string, string] => {
  const now = dayjs();

  switch (rangeType) {
    case "daily":
      return [now.startOf("day").format(), now.endOf("day").format()];

    case "weekly":
      return [now.startOf("week").format(), now.endOf("week").format()];

    case "monthly":
      return [now.startOf("month").format(), now.endOf("month").format()];

    default:
      return [now.startOf("month").format(), now.endOf("month").format()];
  }
};

export const useDateRange = (
  options: UseDateRangeOptions = {},
): UseDateRangeReturn => {
  const {
    initialRange = "monthly",
    customStartDate,
    customEndDate,
    cache = false,
  } = options;

  const dispatch = useTypedDispatch();
  const dateBetweenCache = useTypedSelector((state) => state.cache.dateBetween);
  const dateBetweenTimestamp = useTypedSelector(
    (state) => state.cache.dateBetweenTimestamp,
  );

  const [currentRangeType, setCurrentRangeType] =
    useState<DateRangeType>(initialRange);

  const initialDateRange = useMemo(() => {
    if (cache && dateBetweenCache?.[0] && dateBetweenCache?.[1]) {
      // Check if cache is expired
      if (dateBetweenTimestamp) {
        const now = Date.now();
        const expiryTime =
          dateBetweenTimestamp +
          AppConst.DATE_RANGE_CACHE_EXPIRY_HOURS * 60 * 60 * 1000;

        if (now > expiryTime) {
          // Cache expired, return default range
          return getDateRangeByType(initialRange);
        }
      }
      return dateBetweenCache;
    }
    if (customStartDate && customEndDate) {
      return [customStartDate, customEndDate];
    }
    return getDateRangeByType(initialRange);
  }, [
    cache,
    dateBetweenCache,
    dateBetweenTimestamp,
    initialRange,
    customStartDate,
    customEndDate,
  ]);

  const [dateBetween, setDateBetweenState] = useState<[string, string]>(
    initialDateRange as [string, string],
  );

  const setDateBetween = (date: [Dayjs, Dayjs]) => {
    const newDateBetween: [string, string] = [
      date[0].format(),
      date[1].format(),
    ];
    setDateBetweenState(newDateBetween);
    dispatch(setDateBetweenAction(newDateBetween));
  };

  const setRangeType = (range: DateRangeType) => {
    setCurrentRangeType(range);
    const newDateRange = getDateRangeByType(range);
    setDateBetweenState(newDateRange);
  };

  const resetToRange = (range: DateRangeType) => {
    setCurrentRangeType(range);
    const newDateRange = getDateRangeByType(range);
    setDateBetweenState(newDateRange);
  };

  return {
    dateBetween,
    setDateBetween,
    setRangeType,
    currentRangeType,
    resetToRange,
  };
};
